import React from 'react';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import type { Incident } from '../../services/incidentsApi';
import SeverityBadge from './SeverityBadge';
import StatusBadge from './StatusBadge';
import { presentAreaLabel } from '../../utils/presentAreaLabel';

export interface IncidentDetailHeaderProps {
  displayTitle: string;
  /** One plain-language sentence: what happened + key proof/context. */
  summarySentence: string;
  selectedIncident: Incident | null;
  /** Resolved names for the meta strip; falls back to raw ids. */
  zoneName?: string | null;
  cameraName?: string | null;
  lifecycleLabel: string;
  onClose: () => void;
  /** Shown as compact chips (confidence, recurring, ownership, evidence) when meaningful. */
  contextChips?: Array<{ label: string; tone?: 'default' | 'info' | 'success' | 'warning' | 'error' }> | null;
  /** When false, id is de-emphasized in the meta strip. */
  showIncidentIdInMeta?: boolean;
  /** Pack name when resolved from site policy catalog (policy line stays in summary card). */
  packLabel?: string | null;
  actionMode?: 'close' | 'back';
}

const formatWhen = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const IncidentDetailHeader: React.FC<IncidentDetailHeaderProps> = ({
  displayTitle,
  summarySentence,
  selectedIncident,
  zoneName,
  cameraName,
  lifecycleLabel,
  onClose,
  contextChips,
  showIncidentIdInMeta = true,
  packLabel,
  actionMode = 'close',
}) => {
  const zone = presentAreaLabel(zoneName || (selectedIncident?.zone_id ? String(selectedIncident.zone_id) : ''));
  const camera = cameraName || (selectedIncident?.camera_id ? String(selectedIncident.camera_id) : '');
  const device = selectedIncident?.device_id ? String(selectedIncident.device_id) : '';
  const site = selectedIncident?.site_id ? String(selectedIncident.site_id) : '';

  const metaParts: string[] = [];
  if (site) metaParts.push(`Site ${site}`);
  if (zone) metaParts.push(zone);
  if (camera) metaParts.push(`Camera stream ${camera}`);
  if (device) metaParts.push(`Edge device ${device}`);

  if (selectedIncident) {
    metaParts.push(`First ${formatWhen(selectedIncident?.ts_start || selectedIncident?.created_at)}`);
    metaParts.push(`Last ${formatWhen(selectedIncident?.updated_at || selectedIncident?.ts_end || selectedIncident?.last_edge_update_at)}`);
    if (showIncidentIdInMeta && selectedIncident?.incident_id) {
      metaParts.push(`ID ${selectedIncident.incident_id}`);
    }
  }

  const primaryContextChips = (contextChips || []).filter((chip) => {
    const label = String(chip.label || '');
    return !label.startsWith('Grouped') && label !== 'Related evidence in group';
  });
  const secondaryContextChips = (contextChips || []).filter((chip) => {
    const label = String(chip.label || '');
    return label.startsWith('Grouped') || label === 'Related evidence in group';
  });
  const actionTitle = actionMode === 'back' ? 'Back to review queue' : 'Close incident detail';
  const actionAriaLabel = actionMode === 'back' ? 'Back to review queue' : 'Close';

  return (
    <Box sx={{ mb: 2.5, pr: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
            {displayTitle}
          </Typography>
          {selectedIncident && (
            <Box sx={{ display: 'flex', gap: 0.9, alignItems: 'center', flexWrap: 'wrap', mt: 1.2 }}>
              {selectedIncident.severity && <SeverityBadge severity={selectedIncident.severity} />}
              <StatusBadge status={String(selectedIncident?.status || 'OPEN')} />
              {lifecycleLabel ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  {lifecycleLabel}
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" title={actionTitle} aria-label={actionAriaLabel} sx={{ mt: -0.5 }}>
          {actionMode === 'back' ? <ArrowBackIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
        </IconButton>
      </Box>

      {metaParts.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.1, lineHeight: 1.55 }}>
          {metaParts.join(' · ')}
        </Typography>
      )}

      {String(packLabel || '').trim() ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.85, lineHeight: 1.5, opacity: 0.88 }}>
          Pack: {String(packLabel).trim()}
        </Typography>
      ) : null}

      {summarySentence && (
        <Typography variant="body2" sx={{ mt: 1.7, lineHeight: 1.6, color: 'text.primary', fontWeight: 700 }}>
          {summarySentence}
        </Typography>
      )}

      {primaryContextChips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.3 }}>
          {primaryContextChips.map((chip, index) => (
            <Chip
              key={`${chip.label}-${chip.tone || 'default'}-${index}`}
              size="small"
              color={chip.tone || 'default'}
              label={chip.label}
              variant="outlined"
            />
          ))}
        </Box>
      )}
      {secondaryContextChips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, mt: 0.85 }}>
          {secondaryContextChips.map((chip, index) => (
            <Chip
              key={`${chip.label}-${chip.tone || 'default'}-${index}`}
              size="small"
              color={chip.tone || 'default'}
              label={chip.label}
              variant="outlined"
              sx={{ opacity: 0.72, '& .MuiChip-label': { fontWeight: 600 } }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default IncidentDetailHeader;
